import { Bot, Info, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAssistants } from '../../api/assistants'

interface AssistantPickerProps {
  value: string
  onChange: (assistantId: string) => void
  disabled?: boolean
}

/** Per-org outbound-assistant dropdown. The list is server-filtered to the
 *  org's phone bindings (Worktree A — see ``GET /vapi/assistants``), so the
 *  three render branches are:
 *
 *  - ``reason === 'no_phone_assigned'`` — render an info card + hide the
 *    dropdown. The parent (``CallStatusPanel``) reads the same query via
 *    ``useAssistants`` (TanStack dedupes by key) to also disable the call
 *    button.
 *  - ``assistants.length === 0`` with no reason — phone is bound but no
 *    outbound assistants are wired yet. Show a soft-warning info card and
 *    render the dropdown disabled. Submit will 403 reactively.
 *  - non-empty list — normal select. */
export function AssistantPicker({ value, onChange, disabled }: AssistantPickerProps) {
  const { t } = useTranslation('calls')
  const { data, isLoading, isError, refetch } = useAssistants()

  const isUnprovisioned = data?.reason === 'no_phone_assigned'
  const hasNoBindings =
    !isUnprovisioned && !isLoading && !isError && (data?.assistants.length ?? 0) === 0

  if (isUnprovisioned) {
    return (
      <div>
        <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
          <Bot className="h-3.5 w-3.5" />
          {t('assistant.label')}
        </label>
        <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-sm text-warning">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('assistant.noPhoneAssigned')}</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
        <Bot className="h-3.5 w-3.5" />
        {t('assistant.label')}
      </label>
      <div className="flex items-center gap-2">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || isLoading || isError || hasNoBindings}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          <option value="">{t('assistant.default')}</option>
          {data?.assistants.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-text-muted" />}
      </div>
      {hasNoBindings && (
        <p className="mt-1 flex items-start gap-1.5 text-xs text-text-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{t('assistant.noOutboundAssistants')}</span>
        </p>
      )}
      {isError && (
        <p className="mt-1 text-xs text-warning">
          {t('assistant.loadFailed')}{' '}
          <button
            type="button"
            onClick={() => refetch()}
            className="underline hover:text-warning/80"
          >
            {t('common:actions.retry')}
          </button>
        </p>
      )}
    </div>
  )
}
